import { useQueries, useQuery } from "@tanstack/react-query";
import { ArrowLeft, Printer } from "lucide-react";
import { useParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { useGoBack } from "../../shared/hooks/goBack";
import type {
  ReleaseApproval,
  ReleaseDeployment,
  ReleaseDetail,
  ReleaseDocument,
  ReleaseExecutionStep,
  ReleaseHistoryEntry,
  ReleaseKnownIssue,
  ReleaseModuleLine,
  ReleasePrerequisite,
  ReleaseScopeItem,
  ReleaseValidationCheck,
} from "../../shared/types/api";
import { Button, ErrorState, LoadingState } from "../../shared/ui";
import { PHRASING } from "./ReleaseActivityTab";
import {
  APPROVAL_STATUS_LABELS,
  DEPLOYMENT_STATUS_LABELS,
  PREREQUISITE_STATUS_LABELS,
  RELEASE_STATUS_LABELS,
  RELEASE_TYPE_LABELS,
  SEVERITY_LABELS,
  STEP_STATUS_LABELS,
  VALIDATION_STATUS_LABELS,
  formatReleaseMoment,
  formatTimestamp,
} from "./releaseVocabulary";

/**
 * The whole release, as a document somebody can keep (§42, §44).
 *
 * <p>Every fact here already exists on the release page, spread across eleven tabs. That is the
 * right shape while the release is happening and the wrong one afterwards: an auditor, a customer
 * or somebody joining the team wants one artefact that says what shipped, who did each part, when,
 * and what was known to be wrong at the time.
 *
 * <p>Printed rather than generated server-side. The browser's own Save as PDF produces a real file
 * with real typography and no new dependency, and — more usefully — the page stays readable on
 * screen, so the report is not a thing you can only see by downloading it.
 *
 * <p>Nothing is computed here that is not already recorded. A summary that derived its own figures
 * would drift from the release it claims to describe, and the first disagreement would be
 * discovered by whoever trusted the wrong one.
 */
export function ReleaseSummaryReport() {
  const { releaseId = "" } = useParams();
  const { goBack, hasHistory } = useGoBack(`/releases/${releaseId}`);

  const release = useQuery({
    queryKey: ["release", releaseId],
    queryFn: () => api.get<ReleaseDetail>(`/api/v1/releases/${releaseId}`),
  });

  /* Fired together rather than in sequence: this is one document, and eleven waterfalled
     requests would make it appear a section at a time. */
  const parts = useQueries({
    queries: [
      ["scope", `/api/v1/releases/${releaseId}/scope`],
      ["modules", `/api/v1/releases/${releaseId}/modules`],
      ["prerequisites", `/api/v1/releases/${releaseId}/prerequisites`],
      ["approvals", `/api/v1/releases/${releaseId}/approvals`],
      ["execution-steps", `/api/v1/releases/${releaseId}/execution-steps`],
      ["deployments", `/api/v1/releases/${releaseId}/deployments`],
      ["validation", `/api/v1/releases/${releaseId}/validation`],
      ["known-issues", `/api/v1/releases/${releaseId}/known-issues`],
      ["documents", `/api/v1/releases/${releaseId}/documents`],
      ["history", `/api/v1/releases/${releaseId}/history`],
    ].map(([key, path]) => ({
      queryKey: ["release-report", releaseId, key],
      queryFn: () => api.get<unknown>(path as string),
      enabled: release.data != null,
    })),
  });

  if (release.isPending) {
    return <LoadingState label="Assembling the report…" />;
  }
  if (release.isError) {
    return <ErrorState error={release.error} onRetry={() => void release.refetch()} />;
  }

  const data = release.data;
  const [scope, modules, prerequisites, approvals, steps, deployments, validation, issues,
    documents, history] = parts.map((part) => part.data);

  const scopeItems = (scope as ReleaseScopeItem[] | undefined) ?? [];
  const moduleLines = (modules as ReleaseModuleLine[] | undefined) ?? [];
  const prereqs = (prerequisites as ReleasePrerequisite[] | undefined) ?? [];
  const signOffs = (approvals as ReleaseApproval[] | undefined) ?? [];
  const runbook = (steps as ReleaseExecutionStep[] | undefined) ?? [];
  const attempts = (deployments as ReleaseDeployment[] | undefined) ?? [];
  const checks = (validation as ReleaseValidationCheck[] | undefined) ?? [];
  const knownIssues = (issues as ReleaseKnownIssue[] | undefined) ?? [];
  const files = (documents as ReleaseDocument[] | undefined) ?? [];
  const timeline = (history as ReleaseHistoryEntry[] | undefined) ?? [];

  const when = (iso: string | null) => formatReleaseMoment(iso, data.timeZone);

  return (
    <>
      {/* Screen-only chrome. The printed page is the document, not a screenshot of a screen. */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2 print:hidden">
        <Button
          variant="ghost"
          size="sm"
          icon={<ArrowLeft className="h-4 w-4" />}
          className="-ml-2"
          onClick={goBack}
        >
          {hasHistory ? "Back" : "Back to the release"}
        </Button>
        <Button icon={<Printer className="h-4 w-4" />} onClick={() => window.print()}>
          Download as PDF
        </Button>
      </div>

      <article className="report mx-auto max-w-3xl bg-surface p-8 text-ink-900 print:max-w-none print:p-0">
        <header className="border-b-2 border-ink-900 pb-4">
          <p className="text-xs uppercase tracking-widest text-ink-500">Release report</p>
          <h1 className="mt-1 font-display text-3xl font-semibold">
            {data.releaseName} <span className="text-ink-400">{data.releaseKey}</span>
          </h1>
          <p className="mt-1 text-sm text-ink-600">
            {data.projectName} · version {data.version} · {RELEASE_TYPE_LABELS[data.releaseType]}
            {" · "}
            {RELEASE_STATUS_LABELS[data.status]}
          </p>
          <p className="mt-2 text-xs text-ink-500">
            Planned {when(data.plannedReleaseAt)} · Released {when(data.actualReleaseAt)}
            {" · times in "}
            {data.timeZone}
          </p>
        </header>

        <Section title="People">
          <Facts
            rows={[
              ["Release owner", data.releaseOwner?.fullName],
              ["Release manager", data.releaseManager?.fullName],
              ["Technical lead", data.technicalLead?.fullName],
              ["QA lead", data.qaLead?.fullName],
              ["Deployment owner", data.deploymentOwner?.fullName],
              ["Scope frozen by", data.scopeFrozenBy?.fullName],
            ]}
          />
        </Section>

        {moduleLines.length > 0 && (
          <Section title="Modules shipped">
            <Table head={["Module", "Previous", "Current", ""]}>
              {moduleLines.map((line) => (
                <tr key={line.module.id}>
                  <Td>{line.module.name}</Td>
                  <Td numeric>{line.previousVersion ?? "—"}</Td>
                  <Td numeric>{line.currentVersion ?? "—"}</Td>
                  <Td>{line.changed ? "upgraded" : "carried"}</Td>
                </tr>
              ))}
            </Table>
          </Section>
        )}

        <Section title={`Work in this release (${scopeItems.length})`}>
          {scopeItems.length === 0 ? (
            <Empty>Nothing was mapped into this release.</Empty>
          ) : (
            <Table head={["Item", "Title", "Status", "Assignee", "QA"]}>
              {scopeItems.map((item) => (
                <tr key={item.workItemId}>
                  <Td mono>{item.itemKey}</Td>
                  <Td>{item.title}</Td>
                  <Td>{item.status}</Td>
                  <Td>{item.assigneeName ?? "Unassigned"}</Td>
                  <Td>{item.qaStatus ?? "—"}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Section>

        <Section title={`Pre-requisites (${prereqs.length})`}>
          {prereqs.length === 0 ? (
            <Empty>None were recorded.</Empty>
          ) : (
            <Table head={["What", "Applies to", "Status", "Completed by", "When"]}>
              {prereqs.map((row) => (
                <tr key={row.id}>
                  <Td>
                    {row.title}
                    {!row.mandatory && <span className="text-ink-500"> (optional)</span>}
                  </Td>
                  <Td>{row.environment?.name ?? "Every environment"}</Td>
                  <Td>{PREREQUISITE_STATUS_LABELS[row.status]}</Td>
                  <Td>{row.completedBy?.fullName ?? "—"}</Td>
                  <Td>{row.completedAt ? formatTimestamp(row.completedAt) : "—"}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Section>

        <Section title={`Approvals (${signOffs.length})`}>
          {signOffs.length === 0 ? (
            <Empty>No approvals were requested.</Empty>
          ) : (
            <Table head={["Role", "For", "Approver", "Decision", "When", "Comment"]}>
              {signOffs.map((row) => (
                <tr key={row.id}>
                  <Td>{row.roleLabel}</Td>
                  <Td>{row.environment?.name ?? "The release"}</Td>
                  <Td>{row.approver?.fullName ?? "Nobody named"}</Td>
                  <Td>{APPROVAL_STATUS_LABELS[row.status]}</Td>
                  <Td>{row.respondedAt ? formatTimestamp(row.respondedAt) : "—"}</Td>
                  <Td>{row.comment ?? "—"}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Section>

        <Section title={`Runbook (${runbook.length} steps)`}>
          {runbook.length === 0 ? (
            <Empty>No runbook was planned.</Empty>
          ) : (
            <Table head={["#", "Step", "Environment", "Status", "Ran by", "Started", "Ended"]}>
              {runbook.map((step, position) => (
                <tr key={step.id}>
                  <Td numeric>{position + 1}</Td>
                  <Td>
                    {step.title}
                    {step.executionResult && (
                      <span className="block text-ink-500">{step.executionResult}</span>
                    )}
                    {step.failureReason && (
                      <span className="block text-ink-500">Failed: {step.failureReason}</span>
                    )}
                  </Td>
                  <Td>{step.environment?.name ?? "Every environment"}</Td>
                  <Td>{STEP_STATUS_LABELS[step.status]}</Td>
                  <Td>{step.executedBy?.fullName ?? "—"}</Td>
                  <Td>{step.actualStartAt ? formatTimestamp(step.actualStartAt) : "—"}</Td>
                  <Td>{step.actualEndAt ? formatTimestamp(step.actualEndAt) : "—"}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Section>

        <Section title={`Deployments (${attempts.length})`}>
          {attempts.length === 0 ? (
            <Empty>Nothing was deployed.</Empty>
          ) : (
            <div className="space-y-3">
              {attempts.map((attempt) => (
                <div key={attempt.id} className="break-inside-avoid">
                  <p className="text-sm font-semibold">
                    Attempt {attempt.attemptNo} to {attempt.environment.name} —{" "}
                    {DEPLOYMENT_STATUS_LABELS[attempt.status]}
                  </p>
                  <p className="text-xs text-ink-600">
                    {when(attempt.startedAt)}
                    {attempt.startedBy && ` · started by ${attempt.startedBy.fullName}`}
                    {attempt.completedAt && ` · ended ${formatTimestamp(attempt.completedAt)}`}
                    {attempt.pipelineRunReference && ` · ${attempt.pipelineRunReference}`}
                  </p>
                  {attempt.result && (
                    <p className="mt-1 text-xs text-ink-700">{attempt.result}</p>
                  )}
                  {attempt.artifacts.length > 0 && (
                    <Table head={["Module", "Version", "Outcome", "Notes"]}>
                      {attempt.artifacts.map((carried) => (
                        <tr key={carried.id}>
                          <Td>{carried.module.name}</Td>
                          <Td numeric>{carried.moduleVersion ?? "—"}</Td>
                          <Td>{carried.status}</Td>
                          <Td>{carried.result ?? "—"}</Td>
                        </tr>
                      ))}
                    </Table>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title={`Post-deployment checks (${checks.length})`}>
          {checks.length === 0 ? (
            <Empty>None were planned.</Empty>
          ) : (
            <Table head={["Check", "Status", "Recorded by", "When", "Result"]}>
              {checks.map((check) => (
                <tr key={check.id}>
                  <Td>{check.checkName}</Td>
                  <Td>{VALIDATION_STATUS_LABELS[check.status]}</Td>
                  <Td>{check.completedBy?.fullName ?? "—"}</Td>
                  <Td>{check.completedAt ? formatTimestamp(check.completedAt) : "—"}</Td>
                  <Td>{check.result ?? check.failureReason ?? "—"}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Section>

        {/* What was known to be wrong at the time. The section an incident review turns to first,
            and the one that is worthless if it was written afterwards. */}
        <Section title={`Known issues (${knownIssues.length})`}>
          {knownIssues.length === 0 ? (
            <Empty>Nothing was recorded as known-broken at release time.</Empty>
          ) : (
            <Table head={["Issue", "Severity", "Impact", "Risk accepted by", "When", "Workaround"]}>
              {knownIssues.map((issue) => (
                <tr key={issue.id}>
                  <Td>
                    {issue.title}
                    {issue.workItemKey && (
                      <span className="block font-mono text-ink-500">{issue.workItemKey}</span>
                    )}
                  </Td>
                  <Td>{SEVERITY_LABELS[issue.severity]}</Td>
                  <Td>{issue.impact ?? "—"}</Td>
                  <Td>
                    {issue.riskAccepted ? (issue.acceptedBy?.fullName ?? "accepted") : "not accepted"}
                  </Td>
                  <Td>{issue.acceptedAt ? formatTimestamp(issue.acceptedAt) : "—"}</Td>
                  <Td>{issue.workaround ?? "—"}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Section>

        <Section title={`Documents (${files.length})`}>
          {files.length === 0 ? (
            <Empty>None were attached.</Empty>
          ) : (
            <Table head={["Document", "Type", "File", "Uploaded by"]}>
              {files.map((file) => (
                <tr key={file.id}>
                  <Td>{file.label}</Td>
                  <Td>{file.documentType}</Td>
                  <Td>{file.fileName ?? "not attached"}</Td>
                  <Td>{file.providedBy?.fullName ?? "—"}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Section>

        {/* Last, and complete. Everything above is a summary of decisions; this is the record of
            them, in the order they happened and with the person who made each. */}
        <Section title={`Everything that happened (${timeline.length})`}>
          {timeline.length === 0 ? (
            <Empty>No history was recorded.</Empty>
          ) : (
            <Table head={["When", "Who", "What"]}>
              {timeline.map((entry) => (
                <tr key={entry.id}>
                  <Td>{formatTimestamp(entry.eventTimestamp)}</Td>
                  <Td>{entry.actorName ?? "The system"}</Td>
                  <Td>{describe(entry)}</Td>
                </tr>
              ))}
            </Table>
          )}
        </Section>

        <footer className="mt-8 border-t border-ink-200 pt-3 text-xs text-ink-500">
          {data.releaseKey} · {data.projectName} · report generated{" "}
          {formatTimestamp(new Date().toISOString())}
        </footer>
      </article>
    </>
  );
}

/**
 * One history entry as a sentence.
 *
 * <p>Uses the same phrasing map as the Activity tab rather than its own. Two descriptions of the
 * same event that drifted apart would be worse than one — somebody would find the disagreement
 * and have no way to tell which was right.
 */
function describe(entry: ReleaseHistoryEntry): string {
  const parts = [PHRASING[entry.eventType] ?? "changed something"];
  if (entry.transitionLabel) {
    parts.push(`— ${entry.transitionLabel}`);
  }
  if (entry.workItemKey) {
    parts.push(entry.workItemKey);
  }
  if (entry.fieldName && (entry.oldValue != null || entry.newValue != null)) {
    parts.push(`(${entry.fieldName}: ${entry.oldValue ?? "—"} → ${entry.newValue ?? "—"})`);
  }
  if (entry.reasonComment) {
    parts.push(`· ${entry.reasonComment}`);
  }
  return parts.join(" ");
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6 break-inside-avoid">
      <h2 className="mb-2 border-b border-ink-200 pb-1 font-display text-base font-semibold">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Facts({ rows }: { rows: [string, string | null | undefined][] }) {
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="flex justify-between gap-3 border-b border-ink-100 py-1">
          <dt className="text-ink-600">{label}</dt>
          <dd className="text-right">{value ?? "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead>
          <tr className="border-b border-ink-300">
            {head.map((cell, index) => (
              <th key={index} className="py-1 pr-3 font-semibold uppercase text-ink-600">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">{children}</tbody>
      </table>
    </div>
  );
}

function Td({
  children,
  mono,
  numeric,
}: {
  children: React.ReactNode;
  mono?: boolean;
  numeric?: boolean;
}) {
  return (
    <td
      className={`py-1.5 pr-3 align-top ${mono || numeric ? "font-mono" : ""} ${
        numeric ? "tnum" : ""
      }`}
    >
      {children}
    </td>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-ink-500">{children}</p>;
}
